// Container dla dashboardu handlowca - logika i podpięcie stanu

import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Grid } from '@mui/material';

import { useSelector } from 'store';
import useRoutes from 'routes/useRoutes';
import { WiseTile, WiseTable, WiseChart } from 'components/wise';
import { useGetTraderDashboardTiles } from 'api';
import { ITraderDashboardTile } from 'api/types';

import {
  getTraderDashboardChartParameters,
  traderDashboardChartLoadDataParams
} from './traderDashboardChartConfig';
import {
  getTraderDashboardTasksTableParameters,
  getTraderDashboardTasksTableColumns,
  getTraderDashboardTasksTableActions,
  traderDashboardTasksTableLoadDataParams
} from './traderDashboardTasksTableConfig';
import {
  getTraderDashboardLastOrdersTableParameters,
  getTraderDashboardLastOrdersTableColumns,
  getTraderDashboardLastOrdersTableActions,
  traderDashboardLastOrdersTableLoadDataParams
} from './traderDashboardLastOrdersTableConfig';
import {
  getTraderDashboardRecentlyLoggedTableParameters,
  getTraderDashboardRecentlyLoggedTableColumns,
  getTraderDashboardRecentlyLoggedTableActions,
  traderDashboardRecentlyLoggedTableLoadDataParams
} from './traderDashboardRecentlyLoggedTableConfig';

import styles from 'theme/components/containers/TraderDashboardContainer/TraderDashboardContainer.module.scss';

const TILES_SKELETON_COUNT = 4;

const TraderDashboardContainer = () => {
  const { t } = useTranslation();
  const { urlPrefix } = useRoutes();
  const { isLoading: isReduxLoading } = useSelector((state) => state.traderDashboard);
  const basePath = `${urlPrefix}/trader`;

  const { data: tilesDataFromApi, isLoading: isApiLoading } = useGetTraderDashboardTiles();

  const tilesData = (tilesDataFromApi ?? []) as ITraderDashboardTile[];
  const isLoading = isApiLoading || isReduxLoading;
  const showTilesSkeletons = isLoading && tilesData.length === 0;

  const renderTile = (tile: ITraderDashboardTile, index: number) => {
    switch (tile.type) {
      case 'ratio':
        return (
          <Grid className={styles.gridItem} item xs={12} sm={6} md={4} xl={3} key={index}>
            <WiseTile
              icon={tile.icon}
              title={tile.title}
              type={tile.type}
              data={{ target: tile.data.target ?? 0, current: tile.data.current ?? 0 }}
              isLoading={isLoading}
              info={tile.info}
              href={tile.url ? `${basePath}${tile.url}` : undefined}
            />
          </Grid>
        );
      default:
        return (
          <Grid className={styles.gridItem} item xs={12} sm={6} md={4} xl={3} key={index}>
            <WiseTile
              icon={tile.icon}
              title={tile.title}
              type={tile.type}
              data={{
                text: tile.data.text ?? '',
                textAdditional: tile.data.textAdditional
              }}
              isLoading={isLoading}
              info={tile.info}
              href={tile.url ? `${basePath}${tile.url}` : undefined}
            />
          </Grid>
        );
    }
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderDashboardContainer'
      )}>
      <Grid container spacing={3}>
        {showTilesSkeletons
          ? Array.from({ length: TILES_SKELETON_COUNT }, (_, index) => (
              <Grid className={styles.gridItem} item xs={12} sm={6} md={4} xl={3} key={`skeleton-${index}`}>
                <WiseTile
                  type="text"
                  icon={{ type: 'Placeholder', url: '' }}
                  title=""
                  data={{ text: '' }}
                  isLoading
                />
              </Grid>
            ))
          : tilesData.map(renderTile)}
      </Grid>

      <Grid container spacing={3} sx={{ marginTop: 2 }}>
        <Grid item xs={12} md={6}>
          <WiseTable
            label={t('Lista zadań')}
            componentSymbol="traderDashboardTasksTable"
            parameters={getTraderDashboardTasksTableParameters(t)}
            columns={getTraderDashboardTasksTableColumns(t)}
            actions={getTraderDashboardTasksTableActions(t)}
            loadDataParams={traderDashboardTasksTableLoadDataParams}
            defaultLimit={5}
            isCondensed
            rowKey="id"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WiseChart
            label={t('Marża skumulowana')}
            parameters={getTraderDashboardChartParameters(t)}
            loadDataParams={traderDashboardChartLoadDataParams}
            showLegend
            showTrendLine
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WiseTable
            label={t('Ostatnie zamówienia')}
            componentSymbol="traderDashboardLastOrdersTable"
            parameters={getTraderDashboardLastOrdersTableParameters(t)}
            columns={getTraderDashboardLastOrdersTableColumns(t)}
            actions={getTraderDashboardLastOrdersTableActions(t)}
            loadDataParams={traderDashboardLastOrdersTableLoadDataParams}
            defaultLimit={5}
            isCondensed
            rowKey="id"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WiseTable
            label={t('Ostatnio zalogowani')}
            componentSymbol="traderDashboardRecentlyLoggedTable"
            parameters={getTraderDashboardRecentlyLoggedTableParameters()}
            columns={getTraderDashboardRecentlyLoggedTableColumns(t)}
            actions={getTraderDashboardRecentlyLoggedTableActions()}
            loadDataParams={traderDashboardRecentlyLoggedTableLoadDataParams}
            defaultLimit={5}
            isCondensed
            rowKey="id"
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default TraderDashboardContainer;
